import type { IssueDerivedStatus, IssueSeverity, IssueStatus } from '@/shared/constants/issue-status'

export const GIS_LENSES = ['coverage', 'issues', 'officers'] as const
export type GisLens = (typeof GIS_LENSES)[number]

export const GIS_TABS = ['overview', ...GIS_LENSES] as const
export type GisTab = (typeof GIS_TABS)[number]

export const GIS_PERIODS = ['7d', '30d', '90d', 'fy', 'custom'] as const
export type GisPeriod = (typeof GIS_PERIODS)[number]

/** Periods shown as one-click preset buttons. `custom` is not here — it opens the date-range popover. */
export const GIS_PRESET_PERIODS = ['7d', '30d', '90d', 'fy'] as const
export type GisPresetPeriod = (typeof GIS_PRESET_PERIODS)[number]

/**
 * Query scope shared by the period-aware GIS resources (mandals, trends). `entityId` filters the whole
 * dashboard by entity; `dateFrom`/`dateTo` apply only when `period === 'custom'` (wired in A2). Sites are
 * point-in-time and take `entityId` alone (no period/date).
 */
export interface GisScope {
  period: GisPeriod
  entityId?: string | undefined
  dateFrom?: string | undefined
  dateTo?: string | undefined
}

/** One entity for the GIS entity filter (from `GET /entities`). */
export interface GisEntity {
  id: string
  name: string
  sitesCount: number
  issuesActive: number
}

export const SITE_STATUSES = ['surveyed', 'in_progress', 'overdue', 'never_surveyed'] as const
export type SiteStatus = (typeof SITE_STATUSES)[number]

/**
 * Per-mandal / district deltas vs the immediately-preceding equal-length window.
 * `null` (never `0`) when the previous window has no data to compare against.
 */
export interface GisDeltas {
  coverage: number | null
  activeIssues: number | null
  officers: number | null
}

/** Rolling-window descriptor echoed by the bulk + detail + trends endpoints. */
export interface GisPeriodInfo {
  label: string
  from: string
  to: string
  prevFrom: string
  prevTo: string
}

/** Highest-completion officer for a scope (district summary or one mandal). `null` when no officers. */
export interface GisTopPerformer {
  officerId: string
  name: string
  completion: number // completion_pct
}

/**
 * One mandal row from `GET /dashboard/gis/mandals` (camelCase).
 * Empty mandals come back with `null` metrics (never `0`) — render "—"/grey, never as data.
 * `centroid` is joined client-side from the static MANDAL_CENTROIDS table (the API has no geometry).
 */
export interface MandalAggregate {
  /** Canonical mandal key (e.g. "Turkapalle_M") — joins GeoJSON `Mandal` prop + centroids. Render via prettyName(). */
  name: string
  /**
   * Stable server id (P0). `null` until the backend ships it. Use `mandalId ?? name` as the API key
   * for the detail/trends calls; the NAME stays the GeoJSON/centroid join key (not replaced).
   */
  mandalId: string | null
  centroid: readonly [number, number] // [lon, lat]
  siteCount: number
  coverage: number | null // coverage_pct
  geocoded: number | null // geocoded_pct
  activeIssues: number // active_issues (0 for empty mandals, not null)
  criticalIssues: number
  avgResolutionDays: number | null
  officerCount: number
  officerAvgCompletion: number | null // officer_avg_completion_pct
  // P1 insight fields — server-computed per mandal with the same methodology as `summary`, so the
  // district and mandal scopes reconcile. Empty-mandal contract: pct/score/topPerformer are `null`
  // (never 0); the below-60 count is `0`. Optional on the wire → null-safe until backend deploys.
  criticalSharePct: number | null // critical_share_pct (null when activeIssues === 0)
  healthScore: number | null // health_score (null when the mandal has no sites)
  topPerformer: GisTopPerformer | null // null when the mandal has no officers
  officersBelow60Count: number // officers with completion < 60 (0 when empty, never null)
  surveyedSites: number
  inProgressSites: number
  overdueSites: number
  neverSurveyedSites: number
  // Open-issue tallies feeding the Attention Score (backend request #2; server-computed against the
  // per-severity resolution targets). `openIssues` = all non-closed, `openCritical` = its critical
  // subset, `openOverdue` = open non-critical past its target. Non-critical-on-track = openIssues − openCritical − openOverdue.
  openIssues: number
  openCritical: number
  openOverdue: number
  deltas: GisDeltas
}

/** District rollup from `GET /dashboard/gis/mandals` `data.summary` (precomputed server-side). */
export interface GisSummary {
  mandalCount: number
  siteCount: number
  unmappedSites: number
  coverage: number | null // coverage_pct
  geocoded: number | null // geocoded_pct
  activeIssues: number
  criticalIssues: number
  criticalSharePct: number | null
  avgResolutionDays: number | null
  officerCount: number
  officerAvgCompletion: number | null
  healthScore: number
  topPerformer: GisTopPerformer | null
  officersBelow60Count: number
  deltas: GisDeltas
}

/** Mapped payload of `GET /dashboard/gis/mandals`. */
export interface GisMandalsData {
  district: string
  asOf: string
  period: GisPeriodInfo
  entityId: string | null
  summary: GisSummary
  mandals: MandalAggregate[]
}

/**
 * Lightweight map marker from `GET /dashboard/gis/sites`.
 * `lon`/`lat` are `null` for un-geocoded sites (they get no marker). Sites with a
 * non-canonical mandal are still returned (raw value preserved) so the map never drops them.
 */
export interface GisSite {
  id: string
  name: string
  mandal: string // raw mandal key
  mandalId: string | null // stable mandal id (P0); null when the mandal is non-canonical/unmapped or pre-deploy
  entityId: string | null
  lon: number | null
  lat: number | null
  status: SiteStatus
  completion: number // %
  lastSurvey: string | null // ISO date
  /** Allocated in-charge officer (site.assigned_officer_id → users). `null` when unassigned. */
  officerId: string | null
  officerName: string | null
}

/**
 * One issue row from `GET /issues` (camelCase), scoped to the GIS view's needs (B1/B4).
 * `severity`/`status` reuse the shared issue enums. `category` is the response's `section_title`
 * (the closest thing to a category label), `null` when absent. `siteId`/`siteName` are `null` for
 * issues not bound to a site.
 */
export interface GisIssue {
  id: string
  /** Human ref like "FIM-0001". `null` until the backend includes `issue_ref` on GET /issues. */
  issueRef: string | null
  title: string
  severity: IssueSeverity
  status: IssueStatus
  /** Server-derived display status (v2: open | pending | overdue | completed | completed-overdue).
   *  Optional so lib/test fixtures stay light; the mapper always fills it. */
  derivedStatus?: IssueDerivedStatus
  /** True when pending past its resolution_eta (v2; server-computed, defaults false). */
  isOverdue?: boolean
  /** HOD-committed resolution date (v2); null before acknowledge. */
  resolutionEta?: string | null
  siteId: string | null
  siteName: string | null
  entityName: string | null
  department: string | null // owning line-department from GET /issues (nested `department.name`, or flat `department_name`)
  category: string | null
  /** The survey question that raised this issue (drives "Group by question"); null = manually
   *  raised by the FO with no question linkage — grouped under "No linked question". */
  question: string | null
  createdAt: string // ISO
  /** Primary issue photo from GET /issues (survey-question fallback applied server-side); null when none. */
  photoUrl: string | null
  /** Short excerpt (≤200 chars, trailing … when truncated) from GET /issues; null when absent. */
  description: string | null
}

/** One page of the GIS issue feed: the mapped rows + the server's pagination metadata. */
export interface GisIssuesPage {
  issues: GisIssue[]
  /** Server's total issue count for the window (≥ `issues.length`; exceeds it only when capped). */
  totalCount: number
  /** True when the window holds more than the fetched page (`pagination.has_next`) — i.e. capped. */
  hasMore: boolean
}

/** Mapped payload of `GET /dashboard/gis/trends` (adaptive buckets). */
export interface GisTrends {
  bucketSize: string // 'day' | 'week' | 'month'
  buckets: string[] // date strings, index-aligned with each series
  series: Record<GisLens, (number | null)[]>
}

/**
 * Page-level view-model assembled from the per-resource queries:
 * `mandals`/`summary` ← /mandals (Slice 1), `sites` ← /sites (Slice 2),
 * `trends`/`trendBuckets`/`trendBucketSize` ← /trends (Slice 3).
 */
export interface GisOverview {
  district: string
  asOf: string
  period: GisPeriodInfo
  summary: GisSummary
  mandals: MandalAggregate[]
  sites: GisSite[]
  /** Per-lens series, index-aligned with `trendBuckets`. `null` marks an empty bucket. */
  trends: Record<GisLens, (number | null)[]>
  /** Bucket labels (date strings) for the trend series. */
  trendBuckets: string[]
  /** Bucket granularity: 'day' | 'week' | 'month'. Empty until trends load. */
  trendBucketSize: string
}

/** One row of `GET /reports/officer-performance` (officer × mandal aggregation). */
export interface OfficerPerfRow {
  officerId: string
  officerName: string
  mandal: string
  assigned: number
  completed: number
  missed: number
  completionPct: number | null // completed / (completed + missed); null when denominator is 0
}

/** One page of `GET /reports/officer-performance`. `totalCount` is null when the backend omits meta.pagination. */
export interface OfficerPerfPage {
  rows: OfficerPerfRow[]
  totalCount: number | null
}

export const SURVEY_REPORT_STATUSES = ['pending', 'missed'] as const
export type SurveyReportStatus = (typeof SURVEY_REPORT_STATUSES)[number]

/** One row of `GET /reports/surveys` (flat per-assignment list). */
export interface SurveyReportRow {
  id: string
  foName: string | null
  mandal: string
  surveyName: string
  surveyRef: string
  scheduledDate: string | null // for missed rows this is the missed/lapsed date
  status: string // pending | completed | missed
}

/**
 * Per-status survey counts from `GET /reports/surveys` `meta.counts` (backend handoff §3).
 * Computed over the full filtered set but ignoring the `status` filter, so all pills stay populated
 * regardless of which status the list is showing. `total = pending + missed + completed`.
 */
export interface SurveyReportMeta {
  pending: number
  missed: number
  completed: number
  total: number
}

/** One page of `GET /reports/surveys`. `counts` is the full-set status breakdown (ignores the status filter). */
export interface SurveysReportPage {
  rows: SurveyReportRow[]
  totalCount: number | null
  counts: SurveyReportMeta
}

export const ISSUE_REPORT_GROUPS = ['active', 'resolved', 'pending'] as const
export type IssueReportGroup = (typeof ISSUE_REPORT_GROUPS)[number]

/** One row of `GET /reports/issues`. Aging fields are group-dependent (see backend §3.3). */
export interface IssueAgingRow {
  issueId: string
  issueRef: string
  title: string
  severity: string
  status: string
  foName: string | null
  hodName: string | null
  mandal: string | null
  siteId: string | null
  siteName: string | null
  entityName: string | null
  category: string | null
  createdAt: string
  daysActive: number | null // active group
  resolutionDate: string | null // resolved group (completed_at)
  daysToResolve: number | null // resolved group
}

/** Group counts from `GET /reports/issues` `meta`, for the status-group toggle. */
export interface IssueReportMeta {
  active: number
  resolved: number
  pending: number
}

/** Top categories over the filtered set (from `meta.counts.by_category`, top-8 server-side). */
export interface IssueCategoryCount {
  category: string
  count: number
}

/** One page of `GET /reports/issues`. `meta` is the group-count breakdown (active/resolved/pending). */
export interface IssuesReportPage {
  rows: IssueAgingRow[]
  totalCount: number | null
  meta: IssueReportMeta
  /** Severity tally over the status_group's set; IGNORES the severity filter (all chips stay populated). null when absent. */
  severityCounts: { critical: number; non_critical: number } | null
  /** Top-8 categories; RESPECTS all filters incl. severity. [] when absent. */
  topCategories: IssueCategoryCount[]
}

/** One event in an issue's timeline (from `GET /issues/{id}` `events[]`). */
export interface GisIssueEvent {
  id: string
  type: string // event_type: raised | remark | resolved_by_hod | … | manual_override
  actorName: string | null // null for system events
  actorRole: string | null
  note: string | null
  photoUrls: string[] // from photo_urls (plural); [] when none
  createdAt: string // ISO
}

/** Fully-hydrated issue for the in-panel drill-down (from `GET /issues/{id}`). */
export interface GisIssueDetail {
  id: string
  issueRef: string
  status: IssueStatus
  severity: IssueSeverity
  title: string
  description: string | null
  photoUrl: string | null // the issue's own attachment (raw `photo_url`); null when none
  siteId: string | null
  siteName: string | null
  departmentName: string | null
  hodName: string | null
  /** Site's in-charge officer id (from `site.assigned_officer.id`) — opens the officer accountability view; null when unassigned. */
  officerId?: string | null
  officerName?: string | null
  officerPhone?: string | null
  officerEmail?: string | null
  hodRole?: string | null
  hodPhone?: string | null
  hodEmail?: string | null
  sectionTitle: string | null
  surveyRef: string | null
  raisedByName: string | null
  raisedAt: string // ISO
  resolvedAt: string | null
  completedAt: string | null
  events: GisIssueEvent[]
}
